import React, { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

/*
  Enhanced AI-Network Hub Visualization - Cinematic Web3 Style
  Central RAX AI core with custom-drawn R as the intelligence layer.
  Features: hexagonal frame, rotating rings, orbiting particles, glowing R.
  Canvas 2D — no WebGL, no Three.js. Lightweight and performant.
*/

// Orbital nodes representing the ecosystem RAX coordinates
const ORBITAL_NODES = [
  { label: 'ETHEREUM', angle: 0,    dist: 165, color: '#627EEA', size: 13, type: 'chain' },
  { label: 'BASE',     angle: 51,   dist: 185, color: '#0052FF', size: 12, type: 'chain' },
  { label: 'ARBITRUM', angle: 102,  dist: 160, color: '#28A0F0', size: 12, type: 'chain' },
  { label: 'BNB',      angle: 153,  dist: 180, color: '#F0B90B', size: 12, type: 'chain' },
  { label: 'HYPERLIQUID', angle: 204,  dist: 165, color: '#00D4FF', size: 12, type: 'chain' },
  { label: 'VAULTS',   angle: 255,  dist: 178, color: '#00FFD0', size: 14, type: 'system' },
  { label: 'RISK',     angle: 306,  dist: 170, color: '#FF6B6B', size: 14, type: 'system' },
];

// Data pulse particles that flow between nodes and center
const createPulse = (nodeIndex, direction, offset) => ({
  nodeIndex,
  direction, // 'in' towards center, 'out' from center
  offset,    // timing offset
  speed: 0.35 + Math.random() * 0.2,
});

// Pre-generate pulses for smoother animation
const DATA_PULSES = [];
ORBITAL_NODES.forEach((_, i) => {
  DATA_PULSES.push(createPulse(i, 'in', Math.random() * 3));
  DATA_PULSES.push(createPulse(i, 'out', Math.random() * 3 + 1.5));
  DATA_PULSES.push(createPulse(i, 'in', Math.random() * 3 + 0.8));
  if (i % 2 === 0) {
    DATA_PULSES.push(createPulse(i, 'out', Math.random() * 3 + 2.2));
  }
});

export default function AINetworkVisualization() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  const draw = useCallback((ctx, w, h, t) => {
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;
    const scale = Math.min(w / 520, h / 420, 1.2);

    // Global orbital rotation (very slow drift)
    const globalRotation = t * 0.018;

    // ========== AMBIENT ATMOSPHERE ==========
    // Deep outer glow for cinematic depth
    const atmosphereR = 250 * scale;
    const atmosphereGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, atmosphereR);
    atmosphereGlow.addColorStop(0, 'rgba(0, 212, 255, 0.08)');
    atmosphereGlow.addColorStop(0.3, 'rgba(0, 102, 255, 0.04)');
    atmosphereGlow.addColorStop(0.6, 'rgba(0, 212, 255, 0.015)');
    atmosphereGlow.addColorStop(1, 'rgba(0, 212, 255, 0)');
    ctx.beginPath();
    ctx.arc(cx, cy, atmosphereR, 0, Math.PI * 2);
    ctx.fillStyle = atmosphereGlow;
    ctx.fill();

    // ========== OUTER AMBIENT RINGS ==========
    // Multiple expanding rings for depth
    for (let i = 5; i >= 1; i--) {
      const baseR = (200 + i * 40) * scale;
      const pulse = Math.sin(t * 0.25 + i * 0.4) * 0.1 + 0.9;
      const r = baseR * pulse;
      
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(0, 212, 255, ${0.025 * (6 - i)})`;
      ctx.lineWidth = 0.8;
      ctx.stroke();
    }

    // Rotating dashed orbital ring
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(t * 0.04);
    ctx.setLineDash([6, 16]);
    ctx.beginPath();
    ctx.arc(0, 0, 175 * scale, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0, 212, 255, 0.1)';
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Second orbital ring (counter-rotating)
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-t * 0.025);
    ctx.setLineDash([3, 20]);
    ctx.beginPath();
    ctx.arc(0, 0, 210 * scale, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0, 255, 208, 0.05)';
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // ========== NODE POSITIONS (with orbital motion) ==========
    const nodePositions = ORBITAL_NODES.map((node, i) => {
      const orbitSpeed = 0.015 + (i % 3) * 0.005;
      const wobble = Math.sin(t * 0.5 + i * 0.9) * 4;
      const angleOffset = Math.sin(t * orbitSpeed + i * 0.8) * 3;
      const effectiveAngle = node.angle + angleOffset + (globalRotation * 180 / Math.PI);
      const radians = (effectiveAngle * Math.PI) / 180;
      const dist = (node.dist + wobble) * scale;
      
      return {
        x: cx + Math.cos(radians) * dist,
        y: cy + Math.sin(radians) * dist,
        node,
        radians,
      };
    });

    // ========== CONNECTION LINES ==========
    nodePositions.forEach(({ x, y, node }, i) => {
      // Outer glow line (wider, softer)
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.strokeStyle = `${node.color}0A`;
      ctx.lineWidth = 8;
      ctx.stroke();
      
      // Main connection line with gradient
      const grad = ctx.createLinearGradient(cx, cy, x, y);
      grad.addColorStop(0, 'rgba(0, 212, 255, 0.2)');
      grad.addColorStop(0.5, `${node.color}25`);
      grad.addColorStop(1, `${node.color}40`);
      
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Inner bright line
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x, y);
      ctx.strokeStyle = `${node.color}10`;
      ctx.lineWidth = 3;
      ctx.stroke();
    });

    // ========== CROSS-CONNECTIONS (subtle mesh) ==========
    for (let i = 0; i < nodePositions.length; i++) {
      const j = (i + 1) % nodePositions.length;
      const a = nodePositions[i];
      const b = nodePositions[j];
      
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.03)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // ========== DATA FLOW PULSES ==========
    DATA_PULSES.forEach((pulse) => {
      const { nodeIndex, direction, offset, speed } = pulse;
      const pos = nodePositions[nodeIndex];
      const node = pos.node;
      
      // Calculate pulse position along the line
      const cycleTime = 2.5;
      const rawProgress = ((t * speed + offset) % cycleTime) / cycleTime;
      const progress = direction === 'in' ? rawProgress : 1 - rawProgress;
      
      // Ease the movement
      const eased = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const px = cx + (pos.x - cx) * eased;
      const py = cy + (pos.y - cy) * eased;
      
      // Pulse visibility (fade at endpoints)
      const visibility = Math.sin(progress * Math.PI);
      const alpha = visibility * 0.8;
      
      if (alpha > 0.1) {
        // Pulse glow
        const glowR = 6 * scale;
        const glowGrad = ctx.createRadialGradient(px, py, 0, px, py, glowR);
        glowGrad.addColorStop(0, `${node.color}${Math.round(alpha * 60).toString(16).padStart(2, '0')}`);
        glowGrad.addColorStop(1, `${node.color}00`);
        ctx.beginPath();
        ctx.arc(px, py, glowR, 0, Math.PI * 2);
        ctx.fillStyle = glowGrad;
        ctx.fill();
        
        // Pulse core
        ctx.beginPath();
        ctx.arc(px, py, 2 * scale, 0, Math.PI * 2);
        ctx.fillStyle = `${node.color}${Math.round(alpha * 200).toString(16).padStart(2, '0')}`;
        ctx.fill();
      }
    });

    // ========== ORBITAL NODES (3D SPHERE EFFECT — REFINED) ==========
    // Helper: parse hex color to RGB
    const hexToRgb = (hex) => {
      const c = hex.replace('#', '');
      return { r: parseInt(c.substr(0,2),16), g: parseInt(c.substr(2,2),16), b: parseInt(c.substr(4,2),16) };
    };

    nodePositions.forEach(({ x, y, node }, i) => {
      const breathe = Math.sin(t * 0.7 + i * 0.6) * 0.08 + 1;
      const nodeSize = node.size * scale * breathe;
      const r = nodeSize * 0.52; // smaller sphere radius
      const rgb = hexToRgb(node.color);
      
      // Outer aura (softer, wider)
      const auraR = (node.size * 3 + Math.sin(t * 0.8 + i) * 3) * scale;
      const auraGrad = ctx.createRadialGradient(x, y, 0, x, y, auraR);
      auraGrad.addColorStop(0, `${node.color}18`);
      auraGrad.addColorStop(0.5, `${node.color}08`);
      auraGrad.addColorStop(1, `${node.color}00`);
      ctx.beginPath();
      ctx.arc(x, y, auraR, 0, Math.PI * 2);
      ctx.fillStyle = auraGrad;
      ctx.fill();

      // Subtle drop shadow
      const shadowGrad = ctx.createRadialGradient(x + r * 0.1, y + r * 0.5, 0, x + r * 0.1, y + r * 0.5, r * 0.9);
      shadowGrad.addColorStop(0, 'rgba(0,0,0,0.15)');
      shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.ellipse(x + r * 0.1, y + r * 0.5, r * 0.7, r * 0.3, 0, 0, Math.PI * 2);
      ctx.fillStyle = shadowGrad;
      ctx.fill();

      // Main sphere body — offset gradient (light from upper-left)
      const lightX = x - r * 0.35;
      const lightY = y - r * 0.35;
      const sphereGrad = ctx.createRadialGradient(lightX, lightY, r * 0.05, x, y, r);
      sphereGrad.addColorStop(0, `rgba(${Math.min(255, rgb.r + 80)}, ${Math.min(255, rgb.g + 80)}, ${Math.min(255, rgb.b + 80)}, 0.9)`);
      sphereGrad.addColorStop(0.35, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`);
      sphereGrad.addColorStop(0.7, `rgba(${Math.round(rgb.r * 0.55)}, ${Math.round(rgb.g * 0.55)}, ${Math.round(rgb.b * 0.55)}, 0.85)`);
      sphereGrad.addColorStop(1, `rgba(${Math.round(rgb.r * 0.2)}, ${Math.round(rgb.g * 0.2)}, ${Math.round(rgb.b * 0.2)}, 0.9)`);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = sphereGrad;
      ctx.fill();

      // Rim light (very subtle)
      const rimGrad = ctx.createRadialGradient(x + r * 0.25, y + r * 0.25, r * 0.5, x, y, r);
      rimGrad.addColorStop(0, 'rgba(0,0,0,0)');
      rimGrad.addColorStop(0.85, 'rgba(0,0,0,0)');
      rimGrad.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = rimGrad;
      ctx.fill();

      // Specular highlight (small, soft — not glassy)
      const specX = x - r * 0.25;
      const specY = y - r * 0.25;
      const specR = r * 0.25;
      const specGrad = ctx.createRadialGradient(specX, specY, 0, specX, specY, specR);
      specGrad.addColorStop(0, 'rgba(255,255,255,0.35)');
      specGrad.addColorStop(0.5, 'rgba(255,255,255,0.08)');
      specGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.beginPath();
      ctx.arc(specX, specY, specR, 0, Math.PI * 2);
      ctx.fillStyle = specGrad;
      ctx.fill();

      // Thin outer ring
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = `${node.color}30`;
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // Node label
      ctx.font = `600 ${8 * scale}px 'JetBrains Mono', monospace`;
      ctx.fillStyle = `${node.color}DD`;
      ctx.textAlign = 'center';
      ctx.fillText(node.label, x, y + (node.size * 1.8) * scale);
    });

    // ========== CENTRAL RAX AI CORE - ACTIVE ENGINE ==========
    
    // Animated pulse intensity (breathing effect)
    const pulseIntensity = 0.5 + Math.sin(t * 0.8) * 0.3;
    const fastPulse = 0.7 + Math.sin(t * 2.5) * 0.3;
    
    // Large ambient aura (atmospheric glow - breathing)
    const auraR = (90 + Math.sin(t * 0.4) * 12) * scale;
    const auraGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, auraR);
    auraGrad.addColorStop(0, `rgba(0, 212, 255, ${0.18 * pulseIntensity})`);
    auraGrad.addColorStop(0.3, `rgba(0, 212, 255, ${0.08 * pulseIntensity})`);
    auraGrad.addColorStop(0.6, `rgba(0, 102, 255, ${0.03 * pulseIntensity})`);
    auraGrad.addColorStop(1, 'rgba(0, 212, 255, 0)');
    ctx.beginPath();
    ctx.arc(cx, cy, auraR, 0, Math.PI * 2);
    ctx.fillStyle = auraGrad;
    ctx.fill();

    // Outer breathing ring
    const outerRingR = (58 + Math.sin(t * 0.6) * 5) * scale;
    ctx.beginPath();
    ctx.arc(cx, cy, outerRingR, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(0, 212, 255, ${0.12 * pulseIntensity})`;
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Middle rotating ring segments
    const midRingR = (46 + Math.sin(t * 0.8) * 3) * scale;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-t * 0.25);
    ctx.beginPath();
    ctx.arc(0, 0, midRingR, 0, Math.PI * 0.6);
    ctx.strokeStyle = `rgba(0, 212, 255, ${0.35 * pulseIntensity})`;
    ctx.lineWidth = 2.5 * scale;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, midRingR, Math.PI, Math.PI * 1.6);
    ctx.stroke();
    ctx.restore();

    // Inner pulsing ring
    const innerRingR = (34 + Math.sin(t * 1.2) * 2) * scale;
    ctx.beginPath();
    ctx.arc(cx, cy, innerRingR, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(0, 212, 255, ${0.4 * fastPulse})`;
    ctx.lineWidth = 2 * scale;
    ctx.stroke();

    // Rotating accent arcs (mint color)
    const accentR = innerRingR * 1.15;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(t * 0.4);
    ctx.beginPath();
    ctx.arc(0, 0, accentR, 0, Math.PI * 0.4);
    ctx.strokeStyle = `rgba(0, 255, 208, ${0.55 * fastPulse})`;
    ctx.lineWidth = 3 * scale;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.restore();
    
    // Secondary accent arc
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-t * 0.35 + Math.PI);
    ctx.beginPath();
    ctx.arc(0, 0, accentR * 0.9, 0, Math.PI * 0.25);
    ctx.strokeStyle = `rgba(0, 102, 255, ${0.45 * fastPulse})`;
    ctx.lineWidth = 2 * scale;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.restore();

    // Core radial glow (intense breathing pulse)
    const coreGlowR = (70 + Math.sin(t * 0.7) * 12) * scale;
    const coreGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreGlowR);
    coreGlow.addColorStop(0, `rgba(0, 212, 255, ${0.4 * fastPulse})`);
    coreGlow.addColorStop(0.2, `rgba(0, 212, 255, ${0.2 * fastPulse})`);
    coreGlow.addColorStop(0.5, `rgba(0, 212, 255, ${0.06 * pulseIntensity})`);
    coreGlow.addColorStop(1, 'rgba(0, 212, 255, 0)');
    ctx.beginPath();
    ctx.arc(cx, cy, coreGlowR, 0, Math.PI * 2);
    ctx.fillStyle = coreGlow;
    ctx.fill();

    // Signal processing pulse rings (expanding outward)
    for (let i = 0; i < 3; i++) {
      const pulsePhase = (t * 0.5 + i * 1.2) % 3;
      const pulseR = (20 + pulsePhase * 25) * scale;
      const pulseAlpha = Math.max(0, 0.3 - pulsePhase * 0.1);
      
      if (pulseAlpha > 0) {
        ctx.beginPath();
        ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0, 212, 255, ${pulseAlpha})`;
        ctx.lineWidth = 1.5 * scale;
        ctx.stroke();
      }
    }

    // Core outer circle — 3D sphere
    const coreOuterR = 20 * scale;
    const coreLightX = cx - coreOuterR * 0.3;
    const coreLightY = cy - coreOuterR * 0.3;
    const coreSphereGrad = ctx.createRadialGradient(coreLightX, coreLightY, coreOuterR * 0.05, cx, cy, coreOuterR);
    coreSphereGrad.addColorStop(0, `rgba(80, 240, 255, ${0.7 * fastPulse})`);
    coreSphereGrad.addColorStop(0.3, `rgba(0, 212, 255, ${0.5 * fastPulse})`);
    coreSphereGrad.addColorStop(0.7, `rgba(0, 100, 200, ${0.4 * fastPulse})`);
    coreSphereGrad.addColorStop(1, `rgba(0, 40, 80, ${0.5 * fastPulse})`);
    ctx.beginPath();
    ctx.arc(cx, cy, coreOuterR, 0, Math.PI * 2);
    ctx.fillStyle = coreSphereGrad;
    ctx.fill();

    // Core rim light
    const coreRimGrad = ctx.createRadialGradient(cx + coreOuterR * 0.2, cy + coreOuterR * 0.2, coreOuterR * 0.5, cx, cy, coreOuterR);
    coreRimGrad.addColorStop(0, 'rgba(0,0,0,0)');
    coreRimGrad.addColorStop(0.85, 'rgba(0,0,0,0)');
    coreRimGrad.addColorStop(1, `rgba(0, 212, 255, ${0.2 * fastPulse})`);
    ctx.beginPath();
    ctx.arc(cx, cy, coreOuterR, 0, Math.PI * 2);
    ctx.fillStyle = coreRimGrad;
    ctx.fill();

    // Core specular highlight
    const coreSpecX = cx - coreOuterR * 0.25;
    const coreSpecY = cy - coreOuterR * 0.25;
    const coreSpecR = coreOuterR * 0.35;
    const coreSpecGrad = ctx.createRadialGradient(coreSpecX, coreSpecY, 0, coreSpecX, coreSpecY, coreSpecR);
    coreSpecGrad.addColorStop(0, `rgba(255,255,255,${0.5 * fastPulse})`);
    coreSpecGrad.addColorStop(0.5, `rgba(255,255,255,${0.1 * fastPulse})`);
    coreSpecGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.beginPath();
    ctx.arc(coreSpecX, coreSpecY, coreSpecR, 0, Math.PI * 2);
    ctx.fillStyle = coreSpecGrad;
    ctx.fill();

    // Core inner bright point (pulsing)
    const coreInnerR = (6 + Math.sin(t * 3) * 1) * scale;
    ctx.beginPath();
    ctx.arc(cx, cy, coreInnerR, 0, Math.PI * 2);
    ctx.fillStyle = '#00D4FF';
    ctx.shadowColor = '#00D4FF';
    ctx.shadowBlur = 12 * scale * fastPulse;
    ctx.fill();
    ctx.shadowBlur = 0;

    // Core center white dot
    ctx.beginPath();
    ctx.arc(cx, cy, 3 * scale, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = '#FFFFFF';
    ctx.shadowBlur = 6 * scale;
    ctx.fill();
    ctx.shadowBlur = 0;

    // "RAX AI" label
    ctx.font = `bold ${13 * scale}px 'Syne', sans-serif`;
    ctx.fillStyle = 'rgba(0, 212, 255, 0.9)';
    ctx.textAlign = 'center';
    ctx.fillText('RAX AI', cx, cy + 70 * scale);
    
    // Subtitle
    ctx.font = `${8 * scale}px 'JetBrains Mono', monospace`;
    ctx.fillStyle = 'rgba(0, 212, 255, 0.5)';
    ctx.fillText('INTELLIGENCE ENGINE', cx, cy + 84 * scale);

    // ========== MOUSE INTERACTION ==========
    const mx = mouseRef.current.x;
    const my = mouseRef.current.y;
    if (mx > 0 && my > 0) {
      const dist = Math.sqrt((mx - cx) ** 2 + (my - cy) ** 2);
      if (dist < 220 * scale) {
        const intensity = (1 - dist / (220 * scale)) * 0.3;
        const mouseGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 90 * scale);
        mouseGlow.addColorStop(0, `rgba(0, 212, 255, ${intensity})`);
        mouseGlow.addColorStop(0.4, `rgba(0, 255, 208, ${intensity * 0.4})`);
        mouseGlow.addColorStop(1, 'rgba(0, 212, 255, 0)');
        ctx.beginPath();
        ctx.arc(cx, cy, 90 * scale, 0, Math.PI * 2);
        ctx.fillStyle = mouseGlow;
        ctx.fill();
      }
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    canvas.addEventListener('mousemove', handleMouse);
    canvas.addEventListener('mouseleave', () => {
      mouseRef.current = { x: 0, y: 0 };
    });

    const animate = (time) => {
      const t = time * 0.001;
      const rect = canvas.parentElement.getBoundingClientRect();
      draw(ctx, rect.width, rect.height, t);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouse);
    };
  }, [draw]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto w-full max-w-5xl mt-6"
      style={{ height: '580px' }}
      data-testid="ai-network-viz"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{ cursor: 'default' }}
      />
    </motion.div>
  );
}
